import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  basisPointsToPercent,
  fieldProblems,
  formatBasisPoints,
  percentToBasisPoints,
  type FieldProblem,
  type Permission,
  type TdsProfile,
  type TdsRateOption,
} from '@fpc/shared';
import { fieldOf, problemsOf } from '@/lib/errors';
import { useAuth } from '@/hooks/useAuth';
import { useLayout } from '@/hooks/useMediaQuery';
import { useTdsSections } from '@/hooks/useTdsSections';
import { ResponsiveRecords } from '@/components/ResponsiveRecords';
import {
  EmptyState,
  ErrorState,
  Modal,
  PageHeader,
  Pagination,
  Spinner,
  Table,
} from '@/components/ui';

/** A page title as one record: Companies → Company, General Ledgers → General Ledger. */
const singular = (title: string) => title.replace(/ies$/, 'y').replace(/s$/, '');

export interface FieldDefinition {
  name: string;
  label: string;
  type?:
    | 'text'
    | 'email'
    | 'number'
    | 'date'
    | 'select'
    | 'multiselect'
    | 'checkbox'
    | 'tdsProfiles'
    /** A TDS section's rates: rows of a name and a percentage. */
    | 'tdsRates'
    /** A few mutually exclusive cards, each with a line saying what it is for. */
    | 'choice';
  options?: Array<{ value: string; label: string; hint?: string }>;
  /** Lay a multiselect out in columns instead of one scrolling list. */
  columns?: 2 | 3;
  required?: boolean;
  help?: string;
  /** Hidden when editing (e.g. companyId, which cannot move). */
  createOnly?: boolean;
  /**
   * Shapes the form but is not sent — e.g. which kind of ledger is being
   * added, which decides the fields shown.
   */
  transient?: boolean;
  /**
   * Shown only while this holds for the form's current values — e.g. the
   * vendor picker, which only means something for a Vendor-role login. A
   * hidden field is neither rendered nor sent.
   */
  visibleWhen?(values: Record<string, unknown>): boolean;
  /**
   * Required only while this holds for the form's current values — e.g. an
   * advance's amount, once its date or reference is filled in.
   */
  requiredWhen?(values: Record<string, unknown>): boolean;
  /**
   * Options that depend on the rest of the form — e.g. a department's vertical,
   * which must be one its chosen business unit serves. Used instead of `options`.
   */
  optionsFor?(
    values: Record<string, unknown>,
  ): Array<{ value: string; label: string; hint?: string }>;
  /**
   * A caution about the value as typed that does not stop the save — e.g. a
   * vendor GSTIN that is the company's own. Shown under the field.
   */
  warningFor?(values: Record<string, unknown>): string | undefined;
}

/** A request schema from @fpc/shared — the same one the API validates against. */
export type RequestSchema = Parameters<typeof fieldProblems>[0];

export interface CrudColumn<T> {
  header: string;
  render(row: T): ReactNode;
  align?: 'left' | 'right';
}

/** An extra per-row action, beyond edit — e.g. resending an invitation. */
export interface CrudRowAction {
  label: string;
  run(): Promise<unknown>;
  /** In an edit form: close the form once the action starts, e.g. when it opens a form of its own. */
  closesForm?: boolean;
}

/**
 * Shared scaffolding for the administration screens.
 *
 * Locations, departments, vendors, users and bank accounts are all the same
 * shape — a scoped list with a create/edit form — so they share one component
 * rather than five near-identical pages. Screens with real behaviour
 * (approval rules, roles) are written out separately.
 */
export function CrudPage<T extends { id: string }>({
  title,
  subtitle,
  columns,
  fields,
  permissions,
  queryKey,
  list,
  create,
  update,
  remove,
  restore,
  defaults,
  toFormValues,
  rowActions,
  formActions,
  formNotice,
  formFooter,
  createFooter,
  formReadOnly,
  formColumns,
  embedded = false,
  headerActions,
  schemas,
}: {
  title: string;
  subtitle?: string;
  columns: Array<CrudColumn<T>>;
  fields: FieldDefinition[];
  permissions: { read: Permission; create?: Permission; update?: Permission; delete?: Permission };
  queryKey: string;
  list(
    query: Record<string, unknown>,
  ): Promise<{ items: T[]; page: number; pageSize: number; total: number }>;
  create?(body: Record<string, unknown>): Promise<unknown>;
  update?(id: string, body: Record<string, unknown>): Promise<unknown>;
  remove?(id: string): Promise<unknown>;
  restore?(id: string): Promise<unknown>;
  defaults?: Record<string, unknown>;
  toFormValues?(row: T): Record<string, unknown>;
  /** Buttons on the row, for what someone who cannot open the edit form may still do. */
  rowActions?(row: T): CrudRowAction[];
  /** Buttons in a record's edit form, beside Deactivate — e.g. a vendor's advance. */
  formActions?(row: T): CrudRowAction[];
  /** Shown above a record's edit form, e.g. why it cannot be changed. */
  formNotice?(row: T): ReactNode;
  /** Shown below a record's edit form, e.g. its previous versions. */
  formFooter?(row: T): ReactNode;
  /** Shown below the form that adds a record, e.g. what to add along with it. */
  createFooter?: ReactNode;
  /** A record kept only for reference: its form is shown, but nothing can be saved. */
  formReadOnly?(row: T): boolean;
  /** Lay the create/edit form out in two columns, for records with many fields. */
  formColumns?: 2;
  /** Shown as one section of a larger page, under that page's own title. */
  embedded?: boolean;
  /** Extra buttons beside "Add …", such as an import. */
  headerActions?: ReactNode;
  /**
   * The API's own request schemas, so a form is checked as the user saves it
   * and every problem shows under its field instead of after a round trip.
   */
  schemas?: { create?: RequestSchema; update?: RequestSchema };
}) {
  const { companyId, can } = useAuth();
  const layout = useLayout();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<T | 'new' | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: [queryKey, companyId, page, search],
    queryFn: () => list({ companyId, page, pageSize: 25, q: search || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [queryKey] });

  // Clicking a row opens its edit form, for whoever may change it; the row
  // itself carries no Edit button.
  const rowOpens = !!update && !!permissions.update && can(permissions.update);

  const rowButtons = (row: T) => {
    const actions = rowActions?.(row) ?? [];
    if (!actions.length) return null;
    return (
      <>
        {actions.map((action) => (
          <button
            key={action.label}
            className="text-sm text-brand-600"
            onClick={(event) => {
              event.stopPropagation();
              void action.run().then(invalidate);
            }}
          >
            {action.label}
          </button>
        ))}
      </>
    );
  };
  const actionColumn = data?.items.some((row) => (rowActions?.(row) ?? []).length) ?? false;

  const canCreate = !!create && !!permissions.create && can(permissions.create);
  const addButton =
    canCreate || headerActions ? (
      <div className="flex flex-wrap items-center gap-2">
        {headerActions}
        {canCreate ? (
          <button className="btn-primary" onClick={() => setEditing('new')}>
            Add {inSentence(singular(title))}
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <>
      {embedded ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-sm text-ink-500">{subtitle}</p> : null}
          </div>
          {addButton}
        </div>
      ) : (
        <PageHeader title={title} subtitle={subtitle} actions={addButton} />
      )}

      <div className="card">
        <div className="border-b border-slate-200 px-4 py-3">
          <input
            className="input w-full sm:max-w-xs"
            aria-label={`Search ${inSentence(title)}`}
            placeholder="Search…"
            defaultValue={search}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setSearch((event.target as HTMLInputElement).value);
                setPage(1);
              }
            }}
          />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4">
            <ErrorState error={error} />
          </div>
        ) : !data?.items.length ? (
          <EmptyState title={`No ${inSentence(title)} yet`} />
        ) : (
          <>
            <ResponsiveRecords
              layout={layout}
              openLabel={rowOpens ? 'Edit' : undefined}
              records={data.items.map((row) => {
                // The first column names the record; the rest are its details.
                const [first, ...rest] = columns;
                return {
                  key: row.id,
                  title: first ? first.render(row) : row.id,
                  details: rest.map((column) => ({
                    label: column.header,
                    value: column.render(row),
                  })),
                  actions: rowButtons(row) ?? undefined,
                  onOpen: rowOpens ? () => setEditing(row) : undefined,
                };
              })}
              table={
                <Table>
                  <thead className="thead">
                    <tr>
                      {columns.map((column) => (
                        <th
                          key={column.header}
                          className={`th ${column.align === 'right' ? 'text-right' : ''}`}
                        >
                          {column.header}
                        </th>
                      ))}
                      {actionColumn ? <th className="th" /> : null}
                    </tr>
                  </thead>
                  <tbody className="tbody">
                    {data.items.map((row) => (
                      <tr
                        key={row.id}
                        className={`hover:bg-slate-50 ${rowOpens ? 'cursor-pointer' : ''}`}
                        {...(rowOpens
                          ? {
                              tabIndex: 0,
                              onClick: () => setEditing(row),
                              onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                                if (event.target !== event.currentTarget) return;
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setEditing(row);
                                }
                              },
                            }
                          : {})}
                      >
                        {columns.map((column) => (
                          <td
                            key={column.header}
                            className={`td ${column.align === 'right' ? 'text-right' : ''}`}
                          >
                            {column.render(row)}
                          </td>
                        ))}
                        {actionColumn ? (
                          <td className="td text-right">
                            <span className="flex justify-end gap-3">{rowButtons(row)}</span>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              }
            />
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              onChange={setPage}
            />
          </>
        )}
      </div>

      {editing ? (
        <RecordForm
          title={editing === 'new' ? `Add ${singular(title)}` : `Edit ${singular(title)}`}
          fields={fields.filter((field) => editing === 'new' || !field.createOnly)}
          formColumns={formColumns}
          schema={editing === 'new' ? schemas?.create : schemas?.update}
          initial={
            editing === 'new'
              ? { companyId, ...defaults }
              : (toFormValues?.(editing) ?? (editing as unknown as Record<string, unknown>))
          }
          actions={editing === 'new' ? undefined : formActions?.(editing)}
          notice={editing === 'new' ? undefined : formNotice?.(editing)}
          below={editing === 'new' ? createFooter : formFooter?.(editing)}
          readOnly={editing !== 'new' && !!formReadOnly?.(editing)}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            if (editing === 'new') await create?.(values);
            else await update?.(editing.id, values);
            await invalidate();
            setEditing(null);
          }}
          onDelete={
            editing !== 'new' && remove && permissions.delete && can(permissions.delete)
              ? async () => {
                  await remove(editing.id);
                  await invalidate();
                  setEditing(null);
                }
              : undefined
          }
          onRestore={
            editing !== 'new' && restore && permissions.delete && can(permissions.delete)
              ? async () => {
                  await restore(editing.id);
                  await invalidate();
                  setEditing(null);
                }
              : undefined
          }
          isInactive={
            editing !== 'new' &&
            ((editing as any).active === false ||
              ['INACTIVE', 'SUSPENDED', 'BLOCKED'].includes((editing as any).status as string))
          }
        />
      ) : null}
    </>
  );
}

/** A title as it reads mid-sentence: lower case, except acronyms such as TDS. */
function inSentence(title: string): string {
  return title
    .split(' ')
    .map((word) => (/^[A-Z]{2,}$/.test(word) ? word : word.toLowerCase()))
    .join(' ');
}

export function RecordForm({
  title,
  fields,
  initial,
  onClose,
  onSubmit,
  onDelete,
  onRestore,
  isInactive,
  formColumns,
  schema,
  actions,
  notice,
  below,
  readOnly = false,
}: {
  title: string;
  fields: FieldDefinition[];
  initial: Record<string, unknown>;
  actions?: CrudRowAction[];
  notice?: ReactNode;
  below?: ReactNode;
  readOnly?: boolean;
  onClose(): void;
  onSubmit(values: Record<string, unknown>): Promise<void>;
  onDelete?(): Promise<void>;
  onRestore?(): Promise<void>;
  isInactive?: boolean;
  formColumns?: 2;
  schema?: RequestSchema;
}) {
  // Unique per open form: a form opened over another (a card over its bank
  // account) must not share field ids, or its labels point at the other form.
  const formId = useId();
  const idOf = (name: string) => `${formId}-${name}`;
  const [values, setValues] = useState<Record<string, unknown>>(initial);
  // What is wrong with each field, shown under it until the field is edited.
  const [errors, setErrors] = useState<Record<string, string>>({});

  const visible = fields.filter((field) => !field.visibleWhen || field.visibleWhen(values));
  const visibleNames = visible.map((field) => field.name);
  const labels = Object.fromEntries(fields.map((field) => [field.name, field.label]));

  const change = (name: string, value: unknown) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!(name in current)) return current;
      const { [name]: _cleared, ...rest } = current;
      return rest;
    });
  };

  /** Problems that belong under a field on screen; the rest go in the notice below. */
  const showUnderFields = (problems: FieldProblem[]) => {
    const inline: Record<string, string> = {};
    for (const problem of problems) {
      const field = fieldOf(problem);
      if (visibleNames.includes(field) && !inline[field]) inline[field] = problem.message;
    }
    setErrors(inline);

    // Save sits at the foot of a long form; take the user to the first field to fix.
    const first = visibleNames.find((name) => inline[name]);
    if (first) {
      requestAnimationFrame(() => {
        const element = document.getElementById(idOf(first));
        element?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        element?.focus({ preventScroll: true });
      });
    }
  };

  const save = useMutation({
    onError: (error) => showUnderFields(problemsOf(error)),
    mutationFn: () => {
      // Only send fields the form actually manages, so a PATCH does not
      // echo back read-only server state.
      const payload: Record<string, unknown> = {};
      for (const field of fields) {
        if (field.transient) continue;
        if (field.visibleWhen && !field.visibleWhen(values)) continue;
        const value = values[field.name];
        if (value === undefined || value === '') continue;

        if (field.type === 'number') {
          payload[field.name] = Number(value);
        } else if (field.type === 'select' && (value === 'true' || value === 'false')) {
          payload[field.name] = value === 'true';
        } else {
          payload[field.name] = value;
        }
      }
      if (values.companyId) payload.companyId = values.companyId;

      const problems = [
        ...basicProblems(visible, values),
        ...(schema ? fieldProblems(schema, payload) : []),
      ];
      if (problems.length) {
        // Thrown like an API refusal, so it is shown the same way; nothing is sent.
        return Promise.reject({ status: 422, details: problems });
      }
      return onSubmit(payload);
    },
  });

  const del = useMutation({ mutationFn: () => onDelete!() });
  const restore = useMutation({ mutationFn: () => onRestore!() });

  const twoUp = formColumns === 2;

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide={twoUp}
      footer={
        <>
          {readOnly ? null : isInactive && onRestore ? (
            <button
              className="btn-primary mr-auto"
              disabled={restore.isPending}
              onClick={() => restore.mutate()}
            >
              Activate
            </button>
          ) : !isInactive && onDelete ? (
            <button
              className="btn-danger mr-auto"
              disabled={del.isPending}
              onClick={() => del.mutate()}
            >
              Deactivate
            </button>
          ) : null}
          {actions?.map((action, index) => (
            <button
              key={action.label}
              className={`btn-secondary ${
                index === actions.length - 1 && !(isInactive ? onRestore : onDelete)
                  ? 'mr-auto'
                  : ''
              }`}
              onClick={() => {
                void action.run();
                if (action.closesForm) onClose();
              }}
            >
              {action.label}
            </button>
          ))}
          <button className={`btn-secondary ${readOnly ? 'ml-auto' : ''}`} onClick={onClose}>
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {readOnly ? null : (
            <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          )}
        </>
      }
    >
      {/*
        A long record — a vendor carries contact, tax and bank details — is
        laid out two-up so the whole form is on screen at once instead of
        scrolling. Wide controls still take the full row.
      */}
      {notice ? <div className="mb-4">{notice}</div> : null}
      <fieldset
        disabled={readOnly}
        className={`m-0 min-w-0 border-0 p-0 ${twoUp ? 'grid gap-x-5 gap-y-4 sm:grid-cols-2' : 'space-y-4'}`}
      >
        {visible.map((field) => (
          <div
            key={field.name}
            className={
              twoUp &&
              (field.type === 'multiselect' ||
                field.type === 'tdsProfiles' ||
                field.type === 'tdsRates' ||
                field.type === 'choice')
                ? 'sm:col-span-2'
                : ''
            }
          >
            {field.type === 'choice' ? (
              <span className="label" id={`${idOf(field.name)}-label`}>
                {field.label}
              </span>
            ) : (
              <label className="label" htmlFor={idOf(field.name)}>
                {field.label}
                {field.required || field.requiredWhen?.(values) ? (
                  <span className="text-red-600"> *</span>
                ) : null}
              </label>
            )}

            {field.type === 'choice' ? (
              <div
                role="radiogroup"
                aria-labelledby={`${idOf(field.name)}-label`}
                className="grid gap-2 sm:grid-cols-2"
              >
                {(field.optionsFor?.(values) ?? field.options ?? []).map((option) => {
                  const selected = values[field.name] === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
                        selected
                          ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500/30'
                          : 'border-ink-200 hover:border-ink-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={field.name}
                        value={option.value}
                        checked={selected}
                        onChange={() => change(field.name, option.value)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-medium text-ink-900">
                          {option.label}
                        </span>
                        {option.hint ? (
                          <span className="mt-0.5 block text-xs text-ink-500">{option.hint}</span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : field.type === 'select' ? (
              <select
                id={idOf(field.name)}
                className={errors[field.name] ? 'input input-invalid' : 'input'}
                aria-invalid={Boolean(errors[field.name])}
                aria-describedby={errors[field.name] ? `${idOf(field.name)}-error` : undefined}
                value={String(values[field.name] ?? '')}
                onChange={(event) => change(field.name, event.target.value)}
              >
                <option value="">Select…</option>
                {(field.optionsFor?.(values) ?? field.options)?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'multiselect' ? (
              <CheckboxGroup
                id={idOf(field.name)}
                label={field.label}
                options={field.optionsFor?.(values) ?? field.options ?? []}
                columns={field.columns}
                selected={(values[field.name] as string[] | undefined) ?? []}
                onChange={(next) => change(field.name, next)}
              />
            ) : field.type === 'tdsRates' ? (
              <TdsRatesEditor
                rates={(values[field.name] as TdsRateOption[] | undefined) ?? []}
                onChange={(next) => change(field.name, next)}
              />
            ) : field.type === 'tdsProfiles' ? (
              <TdsProfilesEditor
                profiles={(values[field.name] as TdsProfile[] | undefined) ?? []}
                onChange={(next) => change(field.name, next)}
              />
            ) : field.type === 'checkbox' ? (
              <input
                id={idOf(field.name)}
                type="checkbox"
                checked={Boolean(values[field.name])}
                onChange={(event) => change(field.name, event.target.checked)}
              />
            ) : (
              <input
                id={idOf(field.name)}
                type={field.type ?? 'text'}
                className={errors[field.name] ? 'input input-invalid' : 'input'}
                aria-invalid={Boolean(errors[field.name])}
                aria-describedby={errors[field.name] ? `${idOf(field.name)}-error` : undefined}
                value={String(values[field.name] ?? '')}
                onChange={(event) => change(field.name, event.target.value)}
              />
            )}

            {errors[field.name] ? (
              <p
                id={`${idOf(field.name)}-error`}
                className="mt-1 text-xs text-red-600"
                role="alert"
              >
                {errors[field.name]}
              </p>
            ) : null}
            {field.warningFor?.(values) ? (
              <p className="notice-warning mt-1.5 text-xs">{field.warningFor(values)}</p>
            ) : null}
            {field.help ? <p className="mt-1 text-xs text-slate-500">{field.help}</p> : null}
          </div>
        ))}
      </fieldset>
      {below ? <div className="mt-5">{below}</div> : null}

      {save.error ? (
        <div className="mt-4">
          <ErrorState error={save.error} labels={labels} inlineFields={visibleNames} />
        </div>
      ) : null}
      {del.error ? (
        <div className="mt-4">
          <ErrorState error={del.error} />
        </div>
      ) : null}
      {restore.error ? (
        <div className="mt-4">
          <ErrorState error={restore.error} />
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * What the form can tell without the schema: a required field left empty, an
 * email without an @, a number that is not one.
 */
function basicProblems(fields: FieldDefinition[], values: Record<string, unknown>): FieldProblem[] {
  const problems: FieldProblem[] = [];
  for (const field of fields) {
    const value = values[field.name];
    const empty =
      value === undefined ||
      value === null ||
      (typeof value === 'string' && !value.trim()) ||
      (Array.isArray(value) && value.length === 0);

    if (empty) {
      if (field.required || field.requiredWhen?.(values)) {
        problems.push({
          path: field.name,
          message:
            field.type === 'multiselect'
              ? 'Choose at least one'
              : field.type === 'select'
                ? 'Choose an option'
                : 'This is required',
        });
      }
      continue;
    }
    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) {
      problems.push({ path: field.name, message: 'Enter a valid email address' });
    }
    if (field.type === 'number' && Number.isNaN(Number(value))) {
      problems.push({ path: field.name, message: 'Enter a number' });
    }
  }
  return problems;
}

/**
 * A TDS section's rates, editable as rows: who or what each applies to, and
 * the percentage. Invoices and vendor profiles pick from exactly these.
 */
function TdsRatesEditor({
  rates,
  onChange,
}: {
  rates: TdsRateOption[];
  onChange(next: TdsRateOption[]): void;
}) {
  const update = (index: number, patch: Partial<TdsRateOption>) =>
    onChange(rates.map((rate, position) => (position === index ? { ...rate, ...patch } : rate)));
  return (
    <div className="rounded-md border border-slate-300 bg-white p-3">
      <div className="space-y-2">
        {rates.map((rate, index) => (
          <div key={index} className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="label">Applies to</span>
              <input
                className="input w-56"
                aria-label={`Rate ${index + 1} applies to`}
                placeholder="e.g. Individual / HUF"
                value={rate.label}
                onChange={(event) => update(index, { label: event.target.value })}
              />
            </label>
            <label className="block">
              <span className="label">Rate %</span>
              <RatePercentInput
                basisPoints={rate.rateBasisPoints}
                ariaLabel={`Rate ${index + 1} (%)`}
                onChange={(rateBasisPoints) => update(index, { rateBasisPoints })}
              />
            </label>
            <button
              type="button"
              className="h-[38px] text-sm text-red-600 disabled:cursor-not-allowed disabled:text-ink-300"
              onClick={() => onChange(rates.filter((_, position) => position !== index))}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn-secondary mt-3"
        onClick={() => onChange([...rates, { label: '', rateBasisPoints: 0 }])}
      >
        Add rate
      </button>
    </div>
  );
}

function blankTdsProfile(isDefault: boolean): TdsProfile {
  return { label: '', section: '', rateBasisPoints: 0, isDefault };
}

/**
 * A vendor's TDS defaults, editable as rows — a vendor billing both
 * professional fees and rent needs a different section and rate for each,
 * with exactly one marked as the profile a new invoice is pre-filled with.
 */
function TdsProfilesEditor({
  profiles,
  onChange,
}: {
  profiles: TdsProfile[];
  onChange(next: TdsProfile[]): void;
}) {
  // Each profile's section and rate come from Settings → TDS Sections.
  const { sections, ratesFor } = useTdsSections();
  const update = (index: number, patch: Partial<TdsProfile>) => {
    onChange(
      profiles.map((profile, position) =>
        position === index ? { ...profile, ...patch } : profile,
      ),
    );
  };

  const makeDefault = (index: number) => {
    onChange(profiles.map((profile, position) => ({ ...profile, isDefault: position === index })));
  };

  return (
    <div className="rounded-md border border-slate-300 bg-white p-3">
      {!profiles.length ? (
        <p className="text-sm text-slate-500">No TDS — this vendor's invoices default to gross.</p>
      ) : (
        <div className="space-y-2">
          {profiles.map((profile, index) => (
            // Each input is captioned like the accounting stage's TDS lines, so the
            // row reads without relying on placeholders.
            <div key={index} className="flex flex-wrap items-end gap-2">
              <label className="block">
                <span className="label">Label</span>
                <input
                  className="input w-40"
                  aria-label="TDS profile label"
                  placeholder="e.g. Professional fees"
                  value={profile.label}
                  onChange={(event) => update(index, { label: event.target.value })}
                />
              </label>
              <label className="block">
                <span className="label">Section</span>
                <select
                  className="input w-auto"
                  aria-label="TDS profile section"
                  value={profile.section}
                  onChange={(event) => {
                    const rates = ratesFor(event.target.value);
                    const keep = rates.some(
                      (rate) => rate.rateBasisPoints === profile.rateBasisPoints,
                    );
                    update(index, {
                      section: event.target.value,
                      rateBasisPoints: keep
                        ? profile.rateBasisPoints
                        : rates.length === 1
                          ? rates[0]!.rateBasisPoints
                          : 0,
                    });
                  }}
                >
                  <option value="">Choose…</option>
                  {sections.map((section) => (
                    <option key={section.code} value={section.code}>
                      {section.code} · {section.name}
                    </option>
                  ))}
                  {profile.section && !sections.some((entry) => entry.code === profile.section) ? (
                    <option value={profile.section}>{profile.section}</option>
                  ) : null}
                </select>
              </label>
              <label className="block">
                <span className="label">Rate</span>
                <select
                  className="input w-auto"
                  aria-label="TDS profile rate"
                  value={profile.rateBasisPoints ? String(profile.rateBasisPoints) : ''}
                  disabled={!profile.section}
                  onChange={(event) =>
                    update(index, { rateBasisPoints: Number(event.target.value) || 0 })
                  }
                >
                  <option value="">{profile.section ? 'Choose…' : 'Pick a section'}</option>
                  {ratesFor(profile.section).map((rate) => (
                    <option key={rate.rateBasisPoints} value={String(rate.rateBasisPoints)}>
                      {formatBasisPoints(rate.rateBasisPoints)} · {rate.label}
                    </option>
                  ))}
                  {profile.rateBasisPoints &&
                  !ratesFor(profile.section).some(
                    (rate) => rate.rateBasisPoints === profile.rateBasisPoints,
                  ) ? (
                    <option value={String(profile.rateBasisPoints)}>
                      {formatBasisPoints(profile.rateBasisPoints)} · as recorded
                    </option>
                  ) : null}
                </select>
              </label>
              {/* Input height, so these sit level with the inputs rather than their captions. */}
              <label className="flex h-[38px] items-center gap-1.5 text-sm text-slate-600">
                <input
                  type="radio"
                  name="tdsProfilesDefault"
                  aria-label="TDS profile default"
                  checked={profile.isDefault}
                  onChange={() => makeDefault(index)}
                  className="relative h-4 w-4 shrink-0 cursor-pointer appearance-none rounded border border-ink-300 bg-white checked:border-brand-600 checked:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/25 after:absolute after:inset-1 after:hidden after:rounded-sm after:bg-white checked:after:block"
                />
                Default
              </label>
              <button
                className="h-[38px] text-sm text-red-600 disabled:cursor-not-allowed disabled:text-ink-300"
                onClick={() => onChange(profiles.filter((_, position) => position !== index))}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        className="btn-secondary mt-2"
        onClick={() => onChange([...profiles, blankTdsProfile(profiles.length === 0)])}
      >
        Add TDS profile
      </button>
    </div>
  );
}

/**
 * The rate is typed as a percent, as on the accounting stage, and stored in
 * basis points (10% → 1000) so the deduction is integer arithmetic.
 *
 * The typed text is kept as typed: converting on every keystroke would turn
 * "0." into "0" and make "0.75" impossible to enter.
 */
function RatePercentInput({
  basisPoints,
  onChange,
  ariaLabel = 'TDS profile rate (%)',
}: {
  basisPoints: number;
  onChange(basisPoints: number): void;
  ariaLabel?: string;
}) {
  const [text, setText] = useState(() => String(basisPointsToPercent(basisPoints)));

  // Rows are keyed by position, so removing one hands this input another
  // row's rate: take it whenever it no longer matches what is typed.
  useEffect(() => {
    const typed = Number(text);
    const matches = text.trim() === '' ? basisPoints === 0 : percentOrNull(typed) === basisPoints;
    if (!matches) setText(String(basisPointsToPercent(basisPoints)));
    // Only an outside change to the rate should rewrite the text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basisPoints]);

  return (
    <input
      type="number"
      className="input w-28"
      aria-label={ariaLabel}
      placeholder="e.g. 10"
      step="0.01"
      min="0"
      max="100"
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        onChange(percentOrNull(Number(event.target.value)) ?? 0);
      }}
    />
  );
}

function percentOrNull(percent: number): number | null {
  return Number.isFinite(percent) ? percentToBasisPoints(percent) : null;
}

/**
 * Checkbox list for the multi-value fields.
 *
 * Roles and companies are each a small, fixed set that the administrator has
 * to compare before choosing — a role means nothing without its permission
 * count under it — so every option is on screen with its own checkbox rather
 * than hidden behind a dropdown. A field with a fixed, short list asks for a
 * column count and gets every option at once; the rest fall back to a single
 * list that scrolls, so an unbounded set cannot push the form below the fold.
 */
function CheckboxGroup({
  id,
  label,
  options,
  columns,
  selected,
  onChange,
}: {
  id: string;
  label: string;
  options: Array<{ value: string; label: string; hint?: string }>;
  columns?: 2 | 3;
  selected: string[];
  onChange(next: string[]): void;
}) {
  return (
    <div
      id={id}
      role="group"
      aria-label={label}
      className={`rounded-md border border-slate-300 bg-white p-2 ${
        columns
          ? `grid gap-x-4 ${columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`
          : 'max-h-56 space-y-0.5 overflow-y-auto'
      }`}
    >
      {options.length === 0 ? (
        <p className="px-1 py-0.5 text-sm text-slate-500">Nothing to choose from</p>
      ) : (
        options.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-slate-50"
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={selected.includes(option.value)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, option.value]
                    : selected.filter((entry) => entry !== option.value),
                )
              }
            />
            <span className="min-w-0 flex-1">
              <span className="block">{option.label}</span>
              {/* The hint sits under the name, so a narrow column does not
                  squeeze the two onto one line. */}
              {option.hint ? (
                <span className="block text-xs text-slate-500">{option.hint}</span>
              ) : null}
            </span>
          </label>
        ))
      )}
    </div>
  );
}
